/**
 * The ONE degraded-state expression — "we tried, it didn't work, here is what we know".
 *
 * §0 of the UX guidelines (»Seamless Document Over Cards«) forbids the reflex
 * answer here: a bordered/filled alert panel fencing the bad news off from the
 * page. So this is carried by TYPOGRAPHY instead — a hairline above it (the same
 * separator the surrounding rows use), a quiet uppercase micro-label, the honest
 * German sentence, and the machine reason SUBORDINATE beneath it in the muted
 * tone. No border box, no shadow, no fill, no `surface-panel`.
 *
 * It generalizes the idiom the app already had in two places — the
 * `.opportunity-degraded-cue` (a brief that couldn't be authored) and the
 * `.narrative-degraded-cue` (a summary that came back preliminary) — so a reader
 * meets ONE recognizable shape everywhere something silently fell short, rather
 * than a new invention per surface.
 *
 * `role="status"` makes it a polite live region: a screen reader hears the state
 * without it becoming a visual box. That matches the existing cues, which already
 * carry `role="status"` inside `aria-live` ancestors.
 *
 * Copy is NEVER authored here — every string arrives already resolved through
 * `t()` (ADR I2 rule 2), so this stays a pure render primitive with no VM and no
 * i18n coupling.
 */

import { html, nothing, type TemplateResult } from "@platform/web-kit/render"

export interface DegradedNote {
  /** The micro-label eyebrow — WHAT fell short, in two or three words. */
  label: string
  /** The honest sentence: what this means for the user, and their way forward. */
  message: string
  /** The machine reason (an error string), rendered subordinate. Omit when there is none. */
  detail?: string | undefined
}

/** Render the shared degraded note. Pure lit-html — the host owns layout and spacing. */
export function degradedNote({ label, message, detail }: DegradedNote): TemplateResult {
  return html`
    <p class="degraded-note" role="status" lang="de">
      <span class="degraded-note-label">${label}</span>
      <span class="degraded-note-message">${message}</span>
      ${detail ? html`<span class="degraded-note-detail">${detail}</span>` : nothing}
    </p>
  `
}
